import React from 'react';
import FeedbackBiomarkerCard from './FeedbackBiomarkerCard';
import FeedbackPanelSummaryCard from './FeedbackPanelSummaryCard';
import type { FeedbackMessage, FeedbackRating } from '../../types/feedbackChat';

/**
 * One message.
 *
 * PROVENANCE IS RENDERED, NOT JUST STORED. Three authors are visually distinct:
 * the member, the AI assistant, and Melio's own approved safety copy. The last
 * matters most — when someone is shown emergency wording, they should be able to
 * tell it came from Melio rather than from a language model, because that is
 * exactly the moment the distinction counts.
 *
 * Styling uses the member design tokens (--melio-*) rather than one-off hex
 * values, so this reads as part of the product rather than as a bolted-on chat
 * widget. Approved copy is marked with a terracotta rule rather than a filled
 * alert box: quieter, more in keeping with the rest of the portal, and it does
 * not shout at someone who has just described a symptom.
 */

interface Props {
  message: FeedbackMessage;
  onRate?: (messageId: string, rating: FeedbackRating) => void;
}

export default function FeedbackMessageBubble({ message, onRate }: Props) {
  const isMember = message.authoredBy === 'member';
  const isApprovedCopy = message.authoredBy === 'approved_copy';

  if (isMember) {
    return (
      <div className="flex justify-end">
        <div
          className="rounded-3xl rounded-br-md px-4 py-2.5 max-w-[85%]"
          style={{ backgroundColor: 'var(--melio-ink)', color: 'var(--melio-cream-warm)' }}
        >
          <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-start">
      <div className="max-w-[92%] w-full">
        <p
          className="text-[10px] uppercase tracking-[0.22em] mb-1.5"
          style={{ color: isApprovedCopy ? 'var(--melio-terracotta-deep)' : 'var(--melio-olive-deep)' }}
        >
          {isApprovedCopy ? 'From Melio' : 'Melio Assistant · AI'}
        </p>

        <div
          className={isApprovedCopy ? 'pl-3.5' : 'rounded-3xl rounded-bl-md border px-4 py-3 bg-white'}
          style={isApprovedCopy
            ? { borderLeft: '2px solid var(--melio-terracotta-deep)' }
            : { borderColor: 'var(--melio-border)' }}
        >
          <p
            className="text-sm leading-relaxed whitespace-pre-wrap"
            style={{ color: 'var(--melio-ink)' }}
          >
            {message.content}
          </p>

          {message.card?.type === 'biomarker_card' && (
            <FeedbackBiomarkerCard card={message.card} />
          )}
          {message.card?.type === 'panel_summary_card' && (
            <FeedbackPanelSummaryCard card={message.card} />
          )}
        </div>

        {/* Ratings are offered on AI output only. Rating Melio's own safety
            wording would be meaningless, and inviting it would be worse. */}
        {!isApprovedCopy && onRate && (
          <div className="flex items-center gap-2 mt-2">
            <span className="text-[11px]" style={{ color: 'var(--melio-text-muted)' }}>
              Helpful?
            </span>
            <RatingButton
              label="Yes"
              active={message.rating?.value === 'helpful'}
              onClick={() => onRate(message.id, 'helpful')}
            />
            <RatingButton
              label="No"
              active={message.rating?.value === 'unhelpful'}
              onClick={() => onRate(message.id, 'unhelpful')}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function RatingButton({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className="text-[11px] px-2.5 py-0.5 rounded-full border transition-colors"
      style={{
        borderColor: active ? 'var(--melio-olive)' : 'var(--melio-border)',
        backgroundColor: active ? 'var(--melio-olive-soft)' : 'transparent',
        color: active ? 'var(--melio-olive-deep)' : 'var(--melio-text-muted)',
      }}
    >
      {label}
    </button>
  );
}
